import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";

import type { StudentPortalIdentity } from "../components/student/StudentPortalShell";
import { useAuth } from "./authContext";
import { countRegisteredPrograms } from "./studentDashboard";
import { countActiveTeamsForStudent } from "./studentTeams";
import { getMyTeamUnreadCount, subscribeToTeamUnreadSignals } from "./teamMessages";
import { firstNameFromDisplay, studentPortalRoleLine } from "./studentPortalUi";
import { getMyStudent } from "./students";
import { supabase } from "./supabase";
import type { Student } from "../types/database";

export type StudentPortalShellData = {
  loading: boolean;
  topbarFirstName: string;
  avatarName: string;
  avatarUrl: string | null;
  identity: StudentPortalIdentity | null;
  casesBadgeCount: number;
  teamsBadgeCount: number;
  teamsUnreadCount: number;
  /** Loaded once with shell; use on /student/cases to avoid duplicate student fetch. */
  student: Student | null;
};

/**
 * Module-level cache of last-known shell values per user id.
 *
 * Without this, every page that calls `useStudentPortalShellData()` (dashboard,
 * cases, teams, submissions, profile, settings) creates a fresh component-local
 * state initialised to defaults (e.g. `casesBadgeCount: 0`) and then fetches.
 * The user sees the badge briefly drop to 0 on every navigation, then jump
 * back to the real number when the fetch settles.
 *
 * The cache lets new mounts initialise from the last known values, so the
 * sidebar badges stay stable across navigation. The hook still does a
 * background refetch on focus and writes back to the cache + state if the
 * value actually changed.
 *
 * Lives in module scope (not React Context) intentionally: keeps the change
 * surgical and avoids touching every consumer page. Cleared on sign-out via
 * the `if (!user)` branch in `load()`.
 */
type CachedShellSnapshot = {
  displayName: string;
  avatarUrl: string | null;
  student: Student | null;
  casesBadgeCount: number;
  teamsBadgeCount: number;
  teamsUnreadCount: number;
};
const shellSnapshotCache = new Map<string, CachedShellSnapshot>();

/**
 * Read the last-known shell snapshot for a user, or null if nothing has been
 * cached yet. Pages that don't use `useStudentPortalShellData` directly (the
 * dashboard, profile, competition detail) call this to seed their initial
 * sidebar-badge state, avoiding the 2 → 0 → 2 flicker on navigation.
 */
export function getCachedShellSnapshot(userId: string | null | undefined): CachedShellSnapshot | null {
  if (!userId) return null;
  return shellSnapshotCache.get(userId) ?? null;
}

/**
 * Partial write to the cache. Used by pages that fetch their own counts to
 * keep the shared snapshot in sync, so when the user later navigates to a
 * page that does use `useStudentPortalShellData` the badge starts from the
 * latest known value instead of 0.
 */
export function updateCachedShellSnapshot(
  userId: string | null | undefined,
  patch: Partial<CachedShellSnapshot>
): void {
  if (!userId) return;
  const prev = shellSnapshotCache.get(userId) ?? {
    displayName: "",
    avatarUrl: null,
    student: null,
    casesBadgeCount: 0,
    teamsBadgeCount: 0,
    teamsUnreadCount: 0,
  };
  shellSnapshotCache.set(userId, { ...prev, ...patch });
}

/**
 * Identity + nav badge for student portal shell (sidebar footer + Cases count).
 * Reuses the same reads as the dashboard; no new RPCs.
 */
export function useStudentPortalShellData(): StudentPortalShellData {
  const { user } = useAuth();
  // Seed initial state from the per-user snapshot cache so navigating between
  // student pages doesn't flash sidebar badges from "2" to "0" to "2".
  const initialUid = user?.id ?? null;
  const initialSnapshot = initialUid ? shellSnapshotCache.get(initialUid) : undefined;
  const [loading, setLoading] = useState(!initialSnapshot);
  const [displayName, setDisplayName] = useState(initialSnapshot?.displayName ?? "");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(initialSnapshot?.avatarUrl ?? null);
  const [student, setStudent] = useState<Student | null>(initialSnapshot?.student ?? null);
  const [casesBadgeCount, setCasesBadgeCount] = useState(initialSnapshot?.casesBadgeCount ?? 0);
  const [teamsBadgeCount, setTeamsBadgeCount] = useState(initialSnapshot?.teamsBadgeCount ?? 0);
  const [teamsUnreadCount, setTeamsUnreadCount] = useState(initialSnapshot?.teamsUnreadCount ?? 0);

  // Tracks which user we've completed at least one load for. Subsequent reloads
  // (e.g. on tab focus regain via useFocusEffect) refresh data silently in the
  // background — no loading-state flip means no visible "refresh flash" on the
  // shell when the user switches back to the tab. Pre-seed from the cache so
  // a fresh mount that already has a snapshot is treated as a non-first load
  // and skips the loading-state flip too.
  const loadedUserIdRef = useRef<string | null>(initialSnapshot ? initialUid : null);

  const load = useCallback(async () => {
    if (!user) {
      loadedUserIdRef.current = null;
      shellSnapshotCache.clear();
      setDisplayName("");
      setAvatarUrl(null);
      setStudent(null);
      setCasesBadgeCount(0);
      setTeamsBadgeCount(0);
      setTeamsUnreadCount(0);
      setLoading(false);
      return;
    }
    const uid = user.id;
    const isFirstLoadForUser = loadedUserIdRef.current !== uid;
    if (isFirstLoadForUser) setLoading(true);
    try {
      const [sRow, profileRes, regCount, teamsCount, teamsUnread] = await Promise.all([
        getMyStudent(),
        supabase.from("user_profiles").select("full_name, avatar_url").eq("id", uid).maybeSingle(),
        countRegisteredPrograms(uid),
        countActiveTeamsForStudent(uid),
        getMyTeamUnreadCount(),
      ]);
      // Reference-stable update: only swap the `student` ref when a meaningful
      // field actually changed. Consumers that depend on `shell.student` (vs.
      // `shell.student?.id`) would otherwise see a fresh object on every silent
      // background reload and re-fire their own fetches.
      const nextStudent = (sRow as Student | null) ?? null;
      setStudent((prev) => {
        if (prev === nextStudent) return prev;
        if (!prev || !nextStudent) return nextStudent;
        const a = prev as Student & { program_level?: string | null; graduation_term?: string | null };
        const b = nextStudent as Student & { program_level?: string | null; graduation_term?: string | null };
        if (
          a.id === b.id &&
          a.university === b.university &&
          a.major === b.major &&
          a.graduation_year === b.graduation_year &&
          a.program_level === b.program_level &&
          a.graduation_term === b.graduation_term
        ) {
          return prev;
        }
        return nextStudent;
      });
      const fromProfile =
        typeof profileRes.data?.full_name === "string" ? profileRes.data.full_name.trim() : "";
      const fromMeta =
        typeof user.user_metadata?.full_name === "string"
          ? String(user.user_metadata.full_name).trim()
          : "";
      setDisplayName(fromProfile || fromMeta || user.email || "");
      if (typeof profileRes.data?.avatar_url === "string" && profileRes.data.avatar_url.trim()) {
        setAvatarUrl(profileRes.data.avatar_url);
      } else {
        setAvatarUrl((user.user_metadata?.avatar_url as string | undefined) ?? null);
      }
      setCasesBadgeCount(regCount);
      setTeamsBadgeCount(teamsCount);
      setTeamsUnreadCount(teamsUnread);
      loadedUserIdRef.current = uid;
      // Persist to module cache. The next mount of this hook (a different
      // page, same user) will pick up these values immediately instead of
      // defaulting to 0.
      const fromProfileForCache =
        typeof profileRes.data?.full_name === "string" ? profileRes.data.full_name.trim() : "";
      const fromMetaForCache =
        typeof user.user_metadata?.full_name === "string"
          ? String(user.user_metadata.full_name).trim()
          : "";
      const cachedDisplayName = fromProfileForCache || fromMetaForCache || user.email || "";
      const cachedAvatarUrl =
        typeof profileRes.data?.avatar_url === "string" && profileRes.data.avatar_url.trim()
          ? profileRes.data.avatar_url
          : ((user.user_metadata?.avatar_url as string | undefined) ?? null);
      shellSnapshotCache.set(uid, {
        displayName: cachedDisplayName,
        avatarUrl: cachedAvatarUrl,
        student: nextStudent,
        casesBadgeCount: regCount,
        teamsBadgeCount: teamsCount,
        teamsUnreadCount: teamsUnread,
      });
    } finally {
      if (isFirstLoadForUser) setLoading(false);
    }
  }, [user]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  // Realtime: re-fetch the team unread count whenever (a) any team_messages
  // INSERT happens — RLS only delivers rows for teams the user belongs to —
  // or (b) any team_members UPDATE (which is how the user's own
  // last_read_at moves forward when they read a chat).
  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToTeamUnreadSignals(() => {
      void (async () => {
        const n = await getMyTeamUnreadCount();
        setTeamsUnreadCount(n);
        const uid = user.id;
        const prev = shellSnapshotCache.get(uid);
        if (prev) shellSnapshotCache.set(uid, { ...prev, teamsUnreadCount: n });
      })();
    });
    return () => unsub();
  }, [user]);

  const identity: StudentPortalIdentity | null =
    displayName.trim() === ""
      ? null
      : { fullName: displayName.trim(), subtitleLine: studentPortalRoleLine(student) };

  return {
    loading,
    topbarFirstName: firstNameFromDisplay(displayName || "Student"),
    avatarName: displayName || "Student",
    avatarUrl,
    identity,
    casesBadgeCount,
    teamsBadgeCount,
    teamsUnreadCount,
    student,
  };
}
